import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * TICK-094 — the organizer's guide to the three post-checkout journeys.
 *
 * Written for the person choosing a mode, not for the person who built it. Every section answers
 * one of three questions and nothing else: what the buyer sees, when this is the right choice, and
 * what it costs. The screenshots are captured from the running product by
 * `scripts/capture-guide-shots.mjs` rather than drawn, so a screen that changes shows up here as a
 * stale picture rather than as prose that has quietly stopped being true.
 *
 * The same page is the source of the PDF (`npm run guide:pdf`), so the two can never disagree.
 */

const MODES = [
  {
    key: 'instant',
    n: '1',
    title: 'แสดงบัตรทันที',
    lead: 'ผู้ซื้อเห็นบัตรและ QR บนหน้าจอทันทีที่การชำระเงินได้รับการยืนยัน ระบบไม่ส่งข้อความใด ๆ ออกไป',
    when: [
      'งานที่ต้องการให้ผู้ซื้อได้รับบัตรเร็วที่สุด โดยไม่มีขั้นตอนคั่นกลาง',
      'งานที่ต้องการควบคุมค่าใช้จ่าย SMS ให้เป็นศูนย์',
      'การขายหน้างานหรือขายในช่วงเวลาจำกัด ซึ่งผู้ซื้ออยู่หน้าจอขณะชำระเงิน',
    ],
    cost: 'ไม่มีค่าส่งข้อความต่อคำสั่งซื้อ',
    caution: 'เมื่อผู้ซื้อปิดหน้าจอไปแล้ว ช่องทางกลับมาดูบัตรมีเพียงหน้า “ตั๋วของฉัน” เท่านั้น จึงต้องเปิดใช้งานหน้าดังกล่าวก่อนเลือกโหมดนี้',
  },
  {
    key: 'instant_and_send',
    n: '2',
    title: 'แสดงบัตรทันที และส่งสำเนา',
    lead: 'ผู้ซื้อเห็นบัตรบนหน้าจอทันทีเช่นเดียวกับโหมดที่ 1 จากนั้นระบบส่งลิงก์บัตรเป็นสำเนาตามช่องทางที่ผู้จัดงานกำหนด',
    when: [
      'งานที่ต้องการความเร็วของโหมดที่ 1 แต่ยังต้องการให้ผู้ซื้อมีหลักฐานเก็บไว้ในอีเมลหรือข้อความ',
      'งานที่ผู้ซื้อมักซื้อล่วงหน้าเป็นเวลานาน และอาจกลับมาค้นหาบัตรในภายหลัง',
    ],
    cost: 'มีค่าส่งตามช่องทางที่เลือก — SMS มีค่าใช้จ่ายต่อข้อความ ส่วนอีเมลและ LINE ไม่มี',
    caution: 'หน้าจอที่ผู้ซื้อเห็นเหมือนกับโหมดที่ 1 ทุกประการ ความแตกต่างอยู่ที่สำเนาซึ่งส่งไปยังช่องทางภายนอก',
  },
  {
    key: 'verified_channel',
    n: '3',
    title: 'ยืนยันช่องทางก่อนรับบัตร',
    lead: 'ผู้ซื้อเลือกช่องทางรับบัตร แล้วยืนยันว่าเป็นเจ้าของช่องทางนั้นด้วยรหัส OTP ลิงก์ยืนยัน หรือการเชื่อมบัญชี LINE ระบบจึงจะออกบัตรและส่งไปยังช่องทางที่ยืนยันแล้วเท่านั้น',
    when: [
      'งานที่บัตรมีมูลค่าสูงหรือมีความเสี่ยงต่อการสวมสิทธิ์',
      'งานที่ต้องการหลักฐานว่าบัตรถูกส่งถึงเจ้าของหมายเลขหรืออีเมลนั้นจริง',
      'งานที่มีนโยบายตรวจสอบตัวตนผู้ถือบัตรก่อนเข้างาน',
    ],
    cost: 'มีค่าส่งเฉพาะช่องทางที่ผู้ซื้อเลือก และมีค่า OTP หนึ่งข้อความเมื่อเลือก SMS',
    caution: 'เป็นโหมดที่ปลอดภัยที่สุด แต่มีขั้นตอนเพิ่มหนึ่งขั้น ผู้ซื้อบางส่วนอาจไม่ดำเนินการต่อจนจบ',
  },
] as const;

function Figure({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="guide-figure">
      <img src={src} alt={alt} loading="lazy" />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

export default function GuideTicketDelivery() {
  // Set directly rather than through `usePageTitle`, which prefixes the default event's name. This
  // document belongs to the platform, not to a show, and "MOONFIELD FEST 2026 — คู่มือ…" would say
  // the opposite on the one page an organizer of some other event is most likely to open.
  useEffect(() => { document.title = 'คู่มือการส่งมอบบัตร · JamTick'; }, []);

  return (
    <div className="page guide-page">
      <header className="site-header shell container">
        <Link to="/" className="logo">JAMTICK</Link>
        <span className="guide-header-label">คู่มือผู้จัดงาน</span>
      </header>

      <main className="guide-wrap">
        <p className="guide-eyebrow">คู่มือผู้จัดงาน</p>
        <h1>การส่งมอบบัตรหลังชำระเงิน</h1>
        <p className="guide-lead">
          JamTick รองรับการส่งมอบบัตร 3 รูปแบบ ผู้จัดงานกำหนดได้เองในแต่ละงาน
          เอกสารนี้อธิบายว่าแต่ละรูปแบบทำงานอย่างไร เหมาะกับงานลักษณะใด และมีค่าใช้จ่ายต่างกันอย่างไร
          เพื่อให้เลือกได้ตรงกับลักษณะงาน
        </p>

        <div className="guide-actions">
          <a className="btn-primary" href="/guide/ticket-delivery.pdf" download>
            ดาวน์โหลดคู่มือ (PDF)
          </a>
        </div>

        {/* --- comparison table: the decision, on one screen ------------------------------ */}
        <section className="guide-section">
          <h2>เปรียบเทียบทั้ง 3 รูปแบบ</h2>
          <div className="guide-table-wrap">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>หัวข้อ</th>
                  <th>1 · แสดงบัตรทันที</th>
                  <th>2 · แสดงบัตรทันที + ส่งสำเนา</th>
                  <th>3 · ยืนยันช่องทางก่อน</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>ผู้ซื้อเห็นบัตรเมื่อใด</td>
                  <td>ทันทีที่ยืนยันการชำระเงิน</td>
                  <td>ทันทีที่ยืนยันการชำระเงิน</td>
                  <td>หลังยืนยันช่องทางสำเร็จ</td>
                </tr>
                <tr>
                  <td>ขั้นตอนที่ผู้ซื้อต้องทำเพิ่ม</td>
                  <td>ไม่มี</td>
                  <td>ไม่มี</td>
                  <td>เลือกและยืนยันช่องทาง 1 ขั้นตอน</td>
                </tr>
                <tr>
                  <td>ข้อความที่ระบบส่งออก</td>
                  <td>ไม่ส่ง</td>
                  <td>ส่งสำเนาตามช่องทางที่ผู้จัดงานกำหนด</td>
                  <td>ส่งไปยังช่องทางที่ผู้ซื้อยืนยันแล้วเท่านั้น</td>
                </tr>
                <tr>
                  <td>ค่าใช้จ่าย SMS</td>
                  <td className="guide-ok">ไม่มี</td>
                  <td>มี เมื่อเลือกช่องทาง SMS</td>
                  <td>มี เมื่อผู้ซื้อเลือก SMS (รวมค่า OTP)</td>
                </tr>
                <tr>
                  <td>ระดับการตรวจสอบตัวตน</td>
                  <td>ตามสิทธิ์ของเครื่องที่ชำระเงิน</td>
                  <td>ตามสิทธิ์ของเครื่องที่ชำระเงิน</td>
                  <td className="guide-ok">ยืนยันความเป็นเจ้าของช่องทาง</td>
                </tr>
                <tr>
                  <td>ค่าเริ่มต้นของระบบ</td>
                  <td>—</td>
                  <td>—</td>
                  <td className="guide-ok">เป็นค่าเริ่มต้นของทุกงาน</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="guide-note">
            ทุกงานเริ่มต้นที่รูปแบบที่ 3 การเปลี่ยนรูปแบบมีผลเฉพาะคำสั่งซื้อที่เกิดขึ้นหลังจากบันทึกเท่านั้น
            คำสั่งซื้อเดิมยังคงใช้เงื่อนไขที่กำหนดไว้ ณ เวลาที่ซื้อ
          </p>
        </section>

        {/* --- each mode in turn ---------------------------------------------------------- */}
        {MODES.map((m) => (
          <section className="guide-section" key={m.key} id={m.key}>
            <h2><span className="guide-num">{m.n}</span>{m.title}</h2>
            <p className="guide-lead-sm">{m.lead}</p>

            {m.key === 'instant' && (
              <Figure
                src="/guide/02-instant.png"
                alt="หน้าคำสั่งซื้อแสดงบัตรพร้อม QR ทันทีหลังการชำระเงินได้รับการยืนยัน"
                caption="รูปแบบที่ 1 และ 2 — ผู้ซื้อเห็นบัตรและ QR บนหน้าคำสั่งซื้อทันที หน้าจอของทั้งสองรูปแบบเหมือนกัน ต่างกันที่รูปแบบที่ 2 มีสำเนาส่งตามไปยังช่องทางภายนอก"
              />
            )}
            {m.key === 'verified_channel' && (
              <Figure
                src="/guide/04-verified-channel.png"
                alt="หน้าเลือกช่องทางรับบัตร แสดงตัวเลือก LINE อีเมล และ SMS"
                caption="รูปแบบที่ 3 — ผู้ซื้อเลือกช่องทางและยืนยันตัวตนก่อน ระบบจึงออกบัตรให้"
              />
            )}

            <div className="guide-cols">
              <div>
                <h3>เหมาะกับงานลักษณะใด</h3>
                <ul>{m.when.map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
              <div>
                <h3>ค่าใช้จ่าย</h3>
                <p>{m.cost}</p>
                <h3>ข้อควรทราบ</h3>
                <p>{m.caution}</p>
              </div>
            </div>
          </section>
        ))}

        {/* --- the buyer's way back -------------------------------------------------------- */}
        <section className="guide-section" id="myticket">
          <h2><span className="guide-num">4</span>หน้า “ตั๋วของฉัน”</h2>
          <p className="guide-lead-sm">
            ผู้ซื้อเปิด <code>jamtick.com/myticket</code> เพื่อดูบัตรที่เคยซื้อไว้ได้ทุกงานในหน้าเดียว
            เป็นช่องทางกลับมาดูบัตรที่ไม่หมดอายุ ต่างจากลิงก์ในอีเมลหรือ SMS ซึ่งหมดอายุหลังจบงาน
          </p>
          <Figure
            src="/guide/05-myticket.png"
            alt="หน้าตั๋วของฉัน แสดงรายการบัตรที่ซื้อจากเครื่องนี้"
            caption="เครื่องที่เคยซื้อบัตรจะเห็นรายการทันทีโดยไม่ต้องกรอกรหัส"
          />
          <div className="guide-cols">
            <div>
              <h3>ผู้ซื้อเข้าถึงได้ 2 ทาง</h3>
              <ul>
                <li><strong>เครื่องเดิมที่ซื้อบัตร</strong> — เปิดแล้วเห็นรายการทันที ไม่ต้องกรอกรหัส</li>
                <li><strong>เครื่องอื่น</strong> — ยืนยันหมายเลขโทรศัพท์ด้วยรหัส OTP ทาง SMS แล้วจะเห็นบัตรทุกใบที่ผูกกับหมายเลขนั้น</li>
              </ul>
            </div>
            <div>
              <h3>ข้อควรทราบ</h3>
              <p>
                หากเครื่องหนึ่งเคยซื้อบัตรให้ผู้อื่นด้วยหมายเลขโทรศัพท์ที่ต่างกัน
                ระบบจะไม่แสดงรายการรวมกัน แต่จะขอให้ยืนยันหมายเลขก่อน เพื่อให้แต่ละคนเห็นเฉพาะบัตรของตนเอง
              </p>
              <p>
                หน้านี้เป็นการตั้งค่าระดับแพลตฟอร์ม มีผลกับทุกงานพร้อมกัน
                หากยังไม่เปิดใช้งาน ผู้ซื้อจะไม่สามารถเข้าถึงหน้านี้ได้
              </p>
            </div>
          </div>
        </section>

        {/* --- how to change it ------------------------------------------------------------ */}
        <section className="guide-section" id="setup">
          <h2><span className="guide-num">5</span>วิธีตั้งค่า</h2>

          <h3>กำหนดรูปแบบการส่งมอบบัตรของงาน</h3>
          <p className="guide-lead-sm">
            เข้าหน้าผู้ดูแลของงาน เลือกเมนู <strong>การส่งมอบตั๋ว</strong> เลือกรูปแบบที่ต้องการ
            หากเลือกรูปแบบที่ 2 ให้กำหนดช่องทางส่งสำเนาด้วย แล้วกดบันทึก
            เมนูนี้เปิดให้เฉพาะผู้ใช้สิทธิ์ Owner ของงานนั้น
          </p>
          <Figure
            src="/guide/01-admin-modes.png"
            alt="หน้าตั้งค่าการส่งมอบตั๋วในระบบผู้ดูแลงาน"
            caption="หน้าผู้ดูแลงาน — เมนู “การส่งมอบตั๋ว”"
          />

          <h3>เปิดใช้งานหน้า “ตั๋วของฉัน”</h3>
          <p className="guide-lead-sm">
            เป็นการตั้งค่าระดับแพลตฟอร์ม ดำเนินการโดยผู้ดูแลระบบที่เมนู
            <strong> สวิตช์การส่งมอบตั๋ว</strong> เปิดสวิตช์ <code>/myticket portal</code> แล้วกดบันทึก
          </p>
          <Figure
            src="/guide/06-rollout-switch.png"
            alt="หน้าสวิตช์การส่งมอบตั๋วในระบบผู้ดูแลแพลตฟอร์ม"
            caption="ผู้ดูแลระบบ — เมนู “สวิตช์การส่งมอบตั๋ว”"
          />

          <div className="guide-callout">
            <strong>ลำดับการตั้งค่าที่ต้องปฏิบัติ</strong>
            <p>
              หากต้องการใช้รูปแบบที่ 1 (แสดงบัตรทันที) ต้องเปิดใช้งานหน้า “ตั๋วของฉัน” ก่อนเสมอ
              เนื่องจากรูปแบบดังกล่าวไม่ส่งข้อความใด ๆ ออกไป
              หากหน้าดังกล่าวยังปิดอยู่ ผู้ซื้อที่ปิดหน้าจอไปแล้วจะไม่มีช่องทางกลับมาดูบัตรได้
            </p>
          </div>
        </section>

        <footer className="guide-footer">
          <Link to="/" className="btn-link">← กลับหน้าแรก</Link>
          <span>JamTick · คู่มือการส่งมอบบัตร</span>
        </footer>
      </main>
    </div>
  );
}
